import { ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { CameraFacingMode, CameraService } from 'src/app/core/service/camera.service';
import { CameraError, CameraErrorCode } from 'src/app/core/model/camera-error';
import { BioDocumentCaptureResult, BioDocumentSlot, BioDocumentStep } from '../model/bio-document.model';
import { BioProcessInitResponse } from '../model/bio-process.model';
import { BioDocumentValidationService, BioDocumentValidationResult, BioErrorKind } from '../service/bio-document-validation.service';
import { calculateVideoCrop } from './bio-video-crop.util';
import { analyzeBioCameraFrame, BioCameraQualityStatus, BioCameraRect } from './bio-camera-quality.util';
import { environment } from 'src/environments/environment';

interface SlotCapture {
  file: File;
  preview: string;
}

@Component({
  selector: 'app-bio-document-flow',
  templateUrl: './bio-document-flow.component.html',
  styleUrls: ['./bio-document-flow.component.scss'],
})
export class BioDocumentFlowComponent implements OnDestroy {
  @Output() completed = new EventEmitter<BioDocumentCaptureResult>();
  @Output() validationResult = new EventEmitter<BioDocumentValidationResult>();
  @Output() exit = new EventEmitter<void>();
  /** Comparte el init-process LIVEPROBE con el host para reutilizar el requestId en selfie. */
  @Output() initCompleted = new EventEmitter<BioProcessInitResponse>();
  /** Avisa al host que finish_fail agotó el requestId y requiere un journey nuevo. */
  @Output() journeyTerminated = new EventEmitter<void>();

  @ViewChild('video', { static: false }) videoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('frame', { static: false }) frameRef?: ElementRef<HTMLElement>;
  @ViewChild('canvas', { static: false }) canvasRef?: ElementRef<HTMLCanvasElement>;

  readonly Step = BioDocumentStep;

  step: BioDocumentStep = BioDocumentStep.INTRO;
  activeSlot: BioDocumentSlot = 'front';
  cameraError: CameraError | null = null;
  captureError: string | null = null;
  torchAvailable = false;
  torchOn = false;
  torchPending = false;
  multipleCamerasAvailable = false;
  switchingCamera = false;
  starting = false;
  cameraReady = false;
  validationError = false;
  /** Errores de validación biométrica, separados de los errores reales de cámara. */
  bioValidationError: { kind: BioErrorKind; title: string; message: string } | null = null;
  legacyValidationMessage: string | null = null;
  hostProcessingMessage: string | null = null;
  uploadErrorMessage: string | null = null;
  cameraQualityStatus: BioCameraQualityStatus = 'DOCUMENT_NOT_DETECTED';
  permissionBlocked = false;
  private pendingRetakeSlot: BioDocumentSlot = 'back';
  private transactionId: string | null = null;
  private initResponse: BioProcessInitResponse | null = null;
  private retryableDocumentStatus: 'failed' | 'retry' | null = null;
  private qualityTimer: ReturnType<typeof setInterval> | null = null;
  private pendingQualityStatus: BioCameraQualityStatus | null = null;
  private pendingQualitySince = 0;
  private lastCameraDebugAt = 0;

  private captures: { front?: SlotCapture; back?: SlotCapture } = {};

  get validatingMessage(): string {
    return this.hostProcessingMessage || 'Estamos validando tus datos...';
  }

  get errorPanelTitle(): string {
    return this.bioValidationError?.title || (this.cameraError ? 'No pudimos acceder a la cámara' : 'No pudimos completar la verificación');
  }

  get errorPanelMessage(): string {
    if (this.bioValidationError) {
      return this.bioValidationError.message;
    }
    if (this.cameraError?.message) {
      return this.cameraError.message;
    }
    return 'No pudimos completar la verificación. Intentá nuevamente.';
  }

  constructor(
    private readonly cameraService: CameraService,
    private readonly validationService: BioDocumentValidationService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.stopQualityMonitoring();
    this.cameraService.stopCamera();
    this.torchAvailable = false;
    this.torchOn = false;
    this.torchPending = false;
  }

  get cameraHint(): string {
    if (this.cameraQualityStatus === 'DOCUMENT_NOT_DETECTED') {
      return this.activeSlot === 'front'
        ? 'Ubicá el frente de tu DNI dentro del recuadro'
        : 'Ubicá el dorso de tu DNI dentro del recuadro';
    }
    switch (this.cameraQualityStatus) {
      case 'LOW_LIGHT':
        return 'Necesitamos un poco más de luz';
      case 'OFF_CENTER':
        return 'Centrá tu DNI dentro del recuadro';
      case 'TOO_CLOSE':
        return 'Alejá un poco tu DNI';
      case 'TOO_FAR':
        return 'Acercá un poco más tu DNI';
      case 'READY':
        return 'DNI correctamente ubicado';
      default:
        return this.activeSlot === 'front'
          ? 'Ubicá el frente de tu DNI dentro del recuadro'
          : 'Ubicá el dorso de tu DNI dentro del recuadro';
    }
  }

  start(): void {
    this.transactionId = this.createTransactionId();
    this.runInitProcess();
  }

  /** Inicializa el journey compartido por DNI y selfie antes de pedir acceso a cámara. */
  private runInitProcess(): void {
    this.step = BioDocumentStep.VALIDATING;
    this.validationError = false;
    this.bioValidationError = null;
    this.hostProcessingMessage = 'Estamos preparando la verificación...';
    this.validationService.startLiveprobe(this.transactionId as string).subscribe({
      next: (initRes) => {
        this.initResponse = initRes;
        this.hostProcessingMessage = null;
        this.step = BioDocumentStep.CAMERA_PERMISSION;
        this.initCompleted.emit(initRes);
        this.cd.detectChanges();
      },
      error: (error: BioDocumentValidationResult) => {
        this.hostProcessingMessage = null;
        this.validationError = true;
        this.cameraError = null;
        this.bioValidationError = error?.errorKind === 'PROCESS_EXPIRED'
          ? { kind: 'PROCESS_EXPIRED', title: error.title || 'La verificación expiró', message: error.message || 'El tiempo para completar la validación biométrica finalizó. Volvé a iniciar el proceso.' }
          : {
              kind: 'TECHNICAL',
              title: 'No pudimos completar la verificación',
              message: 'Ocurrió un problema con el servicio de validación. Intentá nuevamente más tarde.',
            };
        this.step = BioDocumentStep.ERROR;
        this.cd.detectChanges();
      },
    });
  }

  async requestPermissionAndStart(): Promise<void> {
    if (!this.transactionId) {
      this.transactionId = this.createTransactionId();
    }
    if (!this.cameraService.isSupported()) {
      this.step = BioDocumentStep.NO_CAMERA;
      return;
    }
    this.activeSlot = 'front';
    await this.openCamera();
  }

  private async openCamera(): Promise<void> {
    this.starting = true;
    this.cameraReady = false;
    this.torchAvailable = false;
    this.torchOn = false;
    this.torchPending = false;
    this.stopQualityMonitoring();
    this.cameraError = null;
    this.cameraQualityStatus = 'DOCUMENT_NOT_DETECTED';
    this.permissionBlocked = false;
    try {
      await this.cameraService.requestCamera('environment');
      const devices = await this.cameraService.getVideoDevices();
      this.multipleCamerasAvailable = devices.length > 1;
      this.torchAvailable = this.cameraService.hasTorch();
      this.step = this.activeSlot === 'front' ? BioDocumentStep.CAPTURE_FRONT : BioDocumentStep.CAPTURE_BACK;
      this.starting = false;
      this.cd.detectChanges();
      this.attachStreamToVideo();
    } catch (error) {
      this.starting = false;
      this.cameraError = error as CameraError;
      const code = (error as CameraError).code;
      if (code === CameraErrorCode.NOT_FOUND) {
        this.step = BioDocumentStep.NO_CAMERA;
      } else if (code === CameraErrorCode.PERMISSION_DENIED) {
        this.step = BioDocumentStep.CAMERA_PERMISSION;
        await this.checkPermissionBlocked();
      } else {
        this.step = BioDocumentStep.ERROR;
      }
    }
  }

  private async checkPermissionBlocked(): Promise<void> {
    try {
      const status = await navigator.permissions?.query({ name: 'camera' as PermissionName });
      this.permissionBlocked = status?.state === 'denied';
    } catch {
      this.permissionBlocked = false;
    }
    this.cd.detectChanges();
  }

  private attachStreamToVideo(): void {
    const video = this.videoRef?.nativeElement;
    const stream = this.cameraService.getStream();
    if (video && stream) {
      video.srcObject = stream;
      video.play().catch(() => {
        if (this.cameraService.getStream() === stream) {
          this.cameraError = { code: CameraErrorCode.UNKNOWN, message: 'No se pudo iniciar la cámara.' };
          this.step = BioDocumentStep.ERROR;
          this.cd.detectChanges();
        }
      });
    }
  }

  onCameraVideoReady(): void {
    const video = this.videoRef?.nativeElement;
    const stream = this.cameraService.getStream();
    if (!video || !stream || video.srcObject !== stream || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      return;
    }
    this.cameraReady = true;
    this.hostProcessingMessage = null;
    this.startQualityMonitoring();
    this.cd.detectChanges();
  }

  private startQualityMonitoring(): void {
    this.stopQualityMonitoring();
    this.qualityTimer = setInterval(() => this.analyzeCameraFrame(), 400);
  }

  private stopQualityMonitoring(): void {
    if (this.qualityTimer !== null) {
      clearInterval(this.qualityTimer);
      this.qualityTimer = null;
    }
    this.pendingQualityStatus = null;
  }

  private analyzeCameraFrame(): void {
    const video = this.videoRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight || this.step < BioDocumentStep.CAPTURE_FRONT || this.step > BioDocumentStep.CAPTURE_BACK) {
      return;
    }
    const width = 320;
    const height = Math.max(1, Math.round((video.videoHeight / video.videoWidth) * width));
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return;
    }
    context.drawImage(video, 0, 0, width, height);
    const crop = this.frameRef?.nativeElement ? calculateVideoCrop(video, this.frameRef.nativeElement, 'cover') : null;
    const guideRect: BioCameraRect = crop
      ? {
          x: (crop.sx / video.videoWidth) * width,
          y: (crop.sy / video.videoHeight) * height,
          width: (crop.sw / video.videoWidth) * width,
          height: (crop.sh / video.videoHeight) * height,
        }
      : { x: 0, y: 0, width, height };
    const result = analyzeBioCameraFrame(context.getImageData(0, 0, width, height), undefined, guideRect);
    const now = Date.now();
    if (environment.type === 'DEV' && now - this.lastCameraDebugAt >= 500) {
      this.lastCameraDebugAt = now;
      console.debug('[BIO CAMERA DEBUG]', {
        brightness: result.luminance,
        documentDetected: result.documentDetected,
        boundingBox: result.boundingBox,
        aspectRatio: result.aspectRatio,
        areaRatio: result.documentAreaRatio,
        centerX: result.centerX,
        centerY: result.centerY,
        centerOffsetX: result.centerOffsetX,
        centerOffsetY: result.centerOffsetY,
        state: result.status,
        videoReadyState: video.readyState,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
      });
    }
    if (!result.documentDetected && result.status !== 'LOW_LIGHT') {
      this.pendingQualityStatus = 'DOCUMENT_NOT_DETECTED';
      this.pendingQualitySince = now;
      if (this.cameraQualityStatus !== 'DOCUMENT_NOT_DETECTED') {
        this.cameraQualityStatus = 'DOCUMENT_NOT_DETECTED';
        this.cd.detectChanges();
      }
      return;
    }
    if (result.status !== this.pendingQualityStatus) {
      this.pendingQualityStatus = result.status;
      this.pendingQualitySince = now;
      return;
    }
    if (result.status !== this.cameraQualityStatus && now - this.pendingQualitySince >= 800) {
      this.cameraQualityStatus = result.status;
      this.cd.detectChanges();
    }
  }

  async toggleTorch(): Promise<void> {
    if (!this.torchAvailable || this.torchPending) {
      return;
    }
    const stream = this.cameraService.getStream();
    this.torchPending = true;
    try {
      const enabled = !this.torchOn;
      const applied = await this.cameraService.setTorch(enabled);
      if (this.cameraService.getStream() !== stream) {
        return;
      }
      if (applied) {
        this.torchOn = enabled;
      } else {
        this.torchAvailable = false;
        this.torchOn = false;
      }
    } catch {
      if (this.cameraService.getStream() === stream) {
        this.torchAvailable = false;
        this.torchOn = false;
      }
    } finally {
      if (this.cameraService.getStream() === stream) {
        this.torchPending = false;
        this.cd.detectChanges();
      }
    }
  }

  async switchCamera(): Promise<void> {
    if (!this.multipleCamerasAvailable || this.switchingCamera) {
      return;
    }
    this.switchingCamera = true;
    this.cameraReady = false;
    this.torchAvailable = false;
    this.torchOn = false;
    this.torchPending = false;
    this.cd.detectChanges();
    try {
      await this.cameraService.switchCamera();
      this.stopQualityMonitoring();
      this.torchAvailable = this.cameraService.hasTorch();
      this.attachStreamToVideo();
    } catch (error) {
      this.cameraError = error as CameraError;
      this.step = BioDocumentStep.ERROR;
      this.cd.detectChanges();
    } finally {
      this.switchingCamera = false;
    }
  }

  capture(): void {
    this.captureError = null;
    const video = this.videoRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;
    const frame = this.frameRef?.nativeElement;
    if (!video || !canvas || !frame) {
      this.captureError = 'No se pudo capturar la foto. Intentá nuevamente.';
      return;
    }
    if (!video.videoWidth || !video.videoHeight) {
      this.captureError = 'No se pudo capturar la foto. Intentá nuevamente.';
      return;
    }

    const crop = calculateVideoCrop(video, frame, 'cover');
    if (!crop) {
      this.captureError = 'No se pudo capturar la foto. Intentá nuevamente.';
      return;
    }

    canvas.width = crop.sw;
    canvas.height = crop.sh;
    const context = canvas.getContext('2d');
    if (!context) {
      this.captureError = 'No se pudo capturar la foto. Intentá nuevamente.';
      return;
    }
    context.drawImage(video, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this.captureError = 'No se pudo capturar la foto. Intentá nuevamente.';
          this.cd.detectChanges();
          return;
        }
        const fileName = this.activeSlot === 'front' ? 'dni-front.jpg' : 'dni-back.jpg';
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        const preview = canvas.toDataURL('image/jpeg', 0.92);
        this.captures[this.activeSlot] = { file, preview };
        this.stopQualityMonitoring();
        this.cameraService.stopCamera();
        this.torchAvailable = false;
        this.torchOn = false;
        this.torchPending = false;
        this.step = this.activeSlot === 'front' ? BioDocumentStep.REVIEW_FRONT : BioDocumentStep.REVIEW_BACK;
        this.cd.detectChanges();
      },
      'image/jpeg',
      0.92,
    );
  }

  get currentPreview(): string | undefined {
    return this.captures[this.activeSlot]?.preview;
  }

  async retake(): Promise<void> {
    delete this.captures[this.activeSlot];
    await this.openCamera();
  }

  async acceptCapture(): Promise<void> {
    if (this.activeSlot === 'front') {
      this.activeSlot = 'back';
      await this.openCamera();
      return;
    }
    this.validate();
  }

  private validate(): void {
    const front = this.captures.front;
    const back = this.captures.back;
    if (!front || !back) {
      return;
    }
    if (!this.initResponse) {
      this.runInitProcess();
      return;
    }
    this.step = BioDocumentStep.VALIDATING;
    this.validationError = false;
    this.retryableDocumentStatus = null;
    this.hostProcessingMessage = 'Estamos validando tus datos...';
    this.uploadErrorMessage = null;
    if (!this.transactionId) {
      this.transactionId = this.createTransactionId();
    }
    this.validationService
      .validateCapturedDocument(this.initResponse.requestId, this.transactionId, front.file, back.file)
      .subscribe({
      next: (result) => {
        this.validationResult.emit(result);
        this.retryableDocumentStatus = result.status === 'failed' || result.status === 'retry' ? result.status : null;
        if (result.success) {
          this.hostProcessingMessage = 'Estamos cargando tus fotos...';
          this.completed.emit({ frontFile: front.file, backFile: back.file });
          this.cd.detectChanges();
        } else {
          this.validationError = true;
          this.cameraError = null;
          this.bioValidationError = {
            kind: result.errorKind || 'TECHNICAL',
            title: result.title || 'No pudimos completar la verificación',
            message: result.message || this.errorPanelMessage,
          };
          this.step = BioDocumentStep.ERROR;
        }
      },
      error: () => {
        this.validationError = true;
        this.cameraError = null;
        this.bioValidationError = {
          kind: 'TECHNICAL',
          title: 'No pudimos completar la verificación',
          message: 'Ocurrió un problema con el servicio de validación. Intentá nuevamente más tarde.',
        };
        this.step = BioDocumentStep.ERROR;
      },
    });
  }

  private createTransactionId(): string {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  /**
   * finish_fail inicia otro journey; errores de imagen/cámara recapturan.
   * Errores de sesión o técnicos revalidan las fotos existentes con el mismo requestId.
   */
  async retryFromError(): Promise<void> {
    if (this.bioValidationError?.kind === 'FINAL_FAILED' || this.bioValidationError?.kind === 'PROCESS_EXPIRED') {
      this.journeyTerminated.emit();
      return;
    }

    const hadValidCaptures = !!this.captures.front && !!this.captures.back;
    const isRealCameraError = !this.bioValidationError && !!this.cameraError;
    const isDocumentValidationError =
      this.bioValidationError?.kind === 'IMAGE' ||
      this.bioValidationError?.kind === 'DOCUMENT' ||
      this.bioValidationError?.kind === 'RETRY';
    const shouldRecapture = isDocumentValidationError || isRealCameraError || !hadValidCaptures;

    this.validationError = false;
    this.cameraError = null;
    this.bioValidationError = null;
    this.hostProcessingMessage = null;
    this.uploadErrorMessage = null;

    if (!shouldRecapture) {
      this.validate();
      return;
    }

    if (this.retryableDocumentStatus && this.initResponse) {
      this.validationService.clearPresignedCache(this.initResponse.requestId, ['DOCUMENT_FRONT', 'DOCUMENT_BACK']);
    }
    this.retryableDocumentStatus = null;
    this.captureError = null;
    this.activeSlot = 'front';
    this.captures = {};
    this.hostProcessingMessage = 'Estamos preparando la verificación...';
    this.step = BioDocumentStep.VALIDATING;
    this.cd.detectChanges();
    await this.openCamera();
  }

  reportHostProcessing(message: string): void {
    this.hostProcessingMessage = message;
    this.uploadErrorMessage = null;
    this.step = BioDocumentStep.VALIDATING;
    this.cd.detectChanges();
  }

  reportHostUploadFailed(message: string): void {
    this.uploadErrorMessage = message;
    this.hostProcessingMessage = null;
    this.step = BioDocumentStep.UPLOAD_FAILED;
    this.cd.detectChanges();
  }

  /** Reenvía las capturas al host para reintentar la carga sin tomar fotos nuevas. */
  retryHostUpload(): void {
    const front = this.captures.front;
    const back = this.captures.back;
    if (!front || !back) {
      return;
    }
    this.uploadErrorMessage = null;
    this.hostProcessingMessage = 'Estamos cargando tus fotos...';
    this.step = BioDocumentStep.VALIDATING;
    this.completed.emit({ frontFile: front.file, backFile: back.file });
    this.cd.detectChanges();
  }

  reportLegacyValidationRejected(message: string, retakeSlot: BioDocumentSlot): void {
    this.legacyValidationMessage = message;
    this.pendingRetakeSlot = retakeSlot;
    this.step = BioDocumentStep.VALIDATION_REJECTED;
  }

  async retryAfterValidationRejected(): Promise<void> {
    this.legacyValidationMessage = null;
    delete this.captures[this.pendingRetakeSlot];
    this.activeSlot = this.pendingRetakeSlot;
    await this.openCamera();
  }

  restartJourney(): void {
    this.stopQualityMonitoring();
    this.cameraService.stopCamera();
    this.torchAvailable = false;
    this.torchOn = false;
    this.torchPending = false;
    this.step = BioDocumentStep.INTRO;
    this.activeSlot = 'front';
    this.cameraError = null;
    this.captureError = null;
    this.validationError = false;
    this.bioValidationError = null;
    this.legacyValidationMessage = null;
    this.hostProcessingMessage = null;
    this.uploadErrorMessage = null;
    this.captures = {};
    this.transactionId = null;
    this.initResponse = null;
    this.retryableDocumentStatus = null;
    this.cd.detectChanges();
  }

  goBack(): void {
    switch (this.step) {
      case BioDocumentStep.INTRO:
        this.exit.emit();
        break;
      case BioDocumentStep.CAMERA_PERMISSION:
        this.step = BioDocumentStep.INTRO;
        break;
      case BioDocumentStep.CAPTURE_FRONT:
        this.stopQualityMonitoring();
        this.cameraService.stopCamera();
        this.torchAvailable = false;
        this.torchOn = false;
        this.torchPending = false;
        this.step = BioDocumentStep.CAMERA_PERMISSION;
        break;
      case BioDocumentStep.REVIEW_FRONT:
        this.activeSlot = 'front';
        this.openCamera();
        break;
      case BioDocumentStep.CAPTURE_BACK:
        this.stopQualityMonitoring();
        this.cameraService.stopCamera();
        this.torchAvailable = false;
        this.torchOn = false;
        this.torchPending = false;
        this.activeSlot = 'front';
        this.step = BioDocumentStep.REVIEW_FRONT;
        break;
      case BioDocumentStep.REVIEW_BACK:
        this.activeSlot = 'back';
        this.openCamera();
        break;
      case BioDocumentStep.NO_CAMERA:
      case BioDocumentStep.ERROR:
      case BioDocumentStep.VALIDATION_REJECTED:
      case BioDocumentStep.UPLOAD_FAILED:
        this.exit.emit();
        break;
      case BioDocumentStep.VALIDATING:
      default:
        break;
    }
  }
}
